// E-Commerce mock data
export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  compareAtPrice?: number;
  category: string;
  status: 'active' | 'draft' | 'archived';
  stock: number;
  sku: string;
  image: string;
  rating: number;
  reviews: number;
  createdAt: Date;
}

export interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  items: OrderItem[];
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';
  paymentStatus: 'paid' | 'pending' | 'failed' | 'refunded';
  shippingAddress: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface OrderItem {
  productId: string;
  productName: string;
  quantity: number;
  price: number;
}

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  totalOrders: number;
  totalSpent: number;
  status: 'active' | 'inactive';
  createdAt: Date;
  lastOrderAt?: Date;
}

export const mockProducts: Product[] = [
  {
    id: 'prod-1',
    name: 'Wireless Bluetooth Headphones',
    description: 'Premium noise-cancelling headphones with 30-hour battery life',
    price: 299.99,
    compareAtPrice: 349.99,
    category: 'Electronics',
    status: 'active',
    stock: 145,
    sku: 'WBH-001',
    image: '/products/headphones.jpg',
    rating: 4.8,
    reviews: 234,
    createdAt: new Date('2024-01-01'),
  },
  {
    id: 'prod-2',
    name: 'Smart Watch Pro',
    description: 'Advanced fitness tracking with heart rate monitor and GPS',
    price: 449.99,
    category: 'Electronics',
    status: 'active',
    stock: 89,
    sku: 'SWP-002',
    image: '/products/smartwatch.jpg',
    rating: 4.6,
    reviews: 189,
    createdAt: new Date('2024-01-05'),
  },
  {
    id: 'prod-3',
    name: 'Ergonomic Office Chair',
    description: 'Adjustable lumbar support with breathable mesh back',
    price: 599.99,
    compareAtPrice: 699.99,
    category: 'Furniture',
    status: 'active',
    stock: 34,
    sku: 'EOC-003',
    image: '/products/chair.jpg',
    rating: 4.9,
    reviews: 456,
    createdAt: new Date('2024-01-10'),
  },
  {
    id: 'prod-4',
    name: 'Mechanical Keyboard RGB',
    description: 'Cherry MX switches with customizable RGB lighting',
    price: 179.99,
    category: 'Electronics',
    status: 'active',
    stock: 267,
    sku: 'MKR-004',
    image: '/products/keyboard.jpg',
    rating: 4.7,
    reviews: 312,
    createdAt: new Date('2024-01-12'),
  },
  {
    id: 'prod-5',
    name: 'Ultra-Wide Monitor 34"',
    description: '3440x1440 resolution with 144Hz refresh rate',
    price: 899.99,
    compareAtPrice: 999.99,
    category: 'Electronics',
    status: 'active',
    stock: 23,
    sku: 'UWM-005',
    image: '/products/monitor.jpg',
    rating: 4.8,
    reviews: 178,
    createdAt: new Date('2024-01-15'),
  },
  {
    id: 'prod-6',
    name: 'Standing Desk Electric',
    description: 'Height adjustable from 28" to 48" with memory presets',
    price: 749.99,
    category: 'Furniture',
    status: 'active',
    stock: 56,
    sku: 'SDE-006',
    image: '/products/desk.jpg',
    rating: 4.5,
    reviews: 234,
    createdAt: new Date('2024-01-18'),
  },
  {
    id: 'prod-7',
    name: 'Wireless Mouse Ergonomic',
    description: 'Vertical design reduces wrist strain, 6 programmable buttons',
    price: 79.99,
    category: 'Electronics',
    status: 'active',
    stock: 412,
    sku: 'WME-007',
    image: '/products/mouse.jpg',
    rating: 4.4,
    reviews: 567,
    createdAt: new Date('2024-01-20'),
  },
  {
    id: 'prod-8',
    name: 'USB-C Docking Station',
    description: 'Triple monitor support with 100W power delivery',
    price: 249.99,
    category: 'Electronics',
    status: 'active',
    stock: 78,
    sku: 'UCD-008',
    image: '/products/dock.jpg',
    rating: 4.6,
    reviews: 145,
    createdAt: new Date('2024-01-22'),
  },
  {
    id: 'prod-9',
    name: 'Webcam 4K HDR',
    description: 'Auto-focus with built-in ring light and noise-cancelling mic',
    price: 199.99,
    compareAtPrice: 229.99,
    category: 'Electronics',
    status: 'active',
    stock: 134,
    sku: 'W4H-009',
    image: '/products/webcam.jpg',
    rating: 4.7,
    reviews: 289,
    createdAt: new Date('2024-01-25'),
  },
  {
    id: 'prod-10',
    name: 'Desk Lamp LED',
    description: 'Adjustable color temperature with wireless charging base',
    price: 89.99,
    category: 'Furniture',
    status: 'draft',
    stock: 0,
    sku: 'DLL-010',
    image: '/products/lamp.jpg',
    rating: 4.3,
    reviews: 98,
    createdAt: new Date('2024-01-28'),
  },
];

export const mockOrders: Order[] = [
  {
    id: 'ord-1',
    orderNumber: 'ORD-2024-001',
    customerId: 'cust-1',
    customerName: 'John Smith',
    customerEmail: 'john.smith@email.com',
    items: [
      { productId: 'prod-1', productName: 'Wireless Bluetooth Headphones', quantity: 1, price: 299.99 },
      { productId: 'prod-4', productName: 'Mechanical Keyboard RGB', quantity: 1, price: 179.99 },
    ],
    subtotal: 479.98,
    tax: 43.20,
    shipping: 0,
    total: 523.18,
    status: 'delivered',
    paymentStatus: 'paid',
    shippingAddress: '123 Main St, New York, NY 10001',
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-20'),
  },
  {
    id: 'ord-2',
    orderNumber: 'ORD-2024-002',
    customerId: 'cust-2',
    customerName: 'Sarah Johnson',
    customerEmail: 'sarah.j@email.com',
    items: [
      { productId: 'prod-3', productName: 'Ergonomic Office Chair', quantity: 1, price: 599.99 },
    ],
    subtotal: 599.99,
    tax: 54.00,
    shipping: 49.99,
    total: 703.98,
    status: 'shipped',
    paymentStatus: 'paid',
    shippingAddress: '456 Oak Ave, Los Angeles, CA 90001',
    createdAt: new Date('2024-01-18'),
    updatedAt: new Date('2024-01-19'),
  },
  {
    id: 'ord-3',
    orderNumber: 'ORD-2024-003',
    customerId: 'cust-3',
    customerName: 'Michael Brown',
    customerEmail: 'mbrown@email.com',
    items: [
      { productId: 'prod-5', productName: 'Ultra-Wide Monitor 34"', quantity: 1, price: 899.99 },
      { productId: 'prod-8', productName: 'USB-C Docking Station', quantity: 1, price: 249.99 },
    ],
    subtotal: 1149.98,
    tax: 103.50,
    shipping: 0,
    total: 1253.48,
    status: 'processing',
    paymentStatus: 'paid',
    shippingAddress: '789 Pine Rd, Chicago, IL 60601',
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-01-20'),
  },
  {
    id: 'ord-4',
    orderNumber: 'ORD-2024-004',
    customerId: 'cust-4',
    customerName: 'Emily Davis',
    customerEmail: 'emily.d@email.com',
    items: [
      { productId: 'prod-2', productName: 'Smart Watch Pro', quantity: 2, price: 449.99 },
    ],
    subtotal: 899.98,
    tax: 81.00,
    shipping: 0,
    total: 980.98,
    status: 'pending',
    paymentStatus: 'pending',
    shippingAddress: '321 Elm St, Houston, TX 77001',
    createdAt: new Date('2024-01-21'),
    updatedAt: new Date('2024-01-21'),
  },
  {
    id: 'ord-5',
    orderNumber: 'ORD-2024-005',
    customerId: 'cust-5',
    customerName: 'David Wilson',
    customerEmail: 'dwilson@email.com',
    items: [
      { productId: 'prod-6', productName: 'Standing Desk Electric', quantity: 1, price: 749.99 },
      { productId: 'prod-3', productName: 'Ergonomic Office Chair', quantity: 1, price: 599.99 },
      { productId: 'prod-7', productName: 'Wireless Mouse Ergonomic', quantity: 1, price: 79.99 },
    ],
    subtotal: 1429.97,
    tax: 128.70,
    shipping: 0,
    total: 1558.67,
    status: 'delivered',
    paymentStatus: 'paid',
    shippingAddress: '654 Maple Dr, Phoenix, AZ 85001',
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-17'),
  },
  {
    id: 'ord-6',
    orderNumber: 'ORD-2024-006',
    customerId: 'cust-6',
    customerName: 'Lisa Anderson',
    customerEmail: 'lisa.a@email.com',
    items: [
      { productId: 'prod-9', productName: 'Webcam 4K HDR', quantity: 1, price: 199.99 },
    ],
    subtotal: 199.99,
    tax: 18.00,
    shipping: 9.99,
    total: 227.98,
    status: 'cancelled',
    paymentStatus: 'refunded',
    shippingAddress: '987 Cedar Ln, Philadelphia, PA 19101',
    createdAt: new Date('2024-01-19'),
    updatedAt: new Date('2024-01-20'),
  },
  {
    id: 'ord-7',
    orderNumber: 'ORD-2024-007',
    customerId: 'cust-7',
    customerName: 'Robert Taylor',
    customerEmail: 'rtaylor@email.com',
    items: [
      { productId: 'prod-1', productName: 'Wireless Bluetooth Headphones', quantity: 2, price: 299.99 },
      { productId: 'prod-7', productName: 'Wireless Mouse Ergonomic', quantity: 2, price: 79.99 },
    ],
    subtotal: 759.96,
    tax: 68.40,
    shipping: 0,
    total: 828.36,
    status: 'shipped',
    paymentStatus: 'paid',
    shippingAddress: '147 Birch Ave, San Antonio, TX 78201',
    createdAt: new Date('2024-01-17'),
    updatedAt: new Date('2024-01-18'),
  },
  {
    id: 'ord-8',
    orderNumber: 'ORD-2024-008',
    customerId: 'cust-8',
    customerName: 'Jennifer Martinez',
    customerEmail: 'jmartinez@email.com',
    items: [
      { productId: 'prod-4', productName: 'Mechanical Keyboard RGB', quantity: 1, price: 179.99 },
      { productId: 'prod-7', productName: 'Wireless Mouse Ergonomic', quantity: 1, price: 79.99 },
      { productId: 'prod-9', productName: 'Webcam 4K HDR', quantity: 1, price: 199.99 },
    ],
    subtotal: 459.97,
    tax: 41.40,
    shipping: 0,
    total: 501.37,
    status: 'processing',
    paymentStatus: 'paid',
    shippingAddress: '258 Walnut St, San Diego, CA 92101',
    createdAt: new Date('2024-01-21'),
    updatedAt: new Date('2024-01-21'),
  },
];

export const mockCustomers: Customer[] = [
  {
    id: 'cust-1',
    name: 'John Smith',
    email: 'john.smith@email.com',
    phone: '+1 (555) 123-4567',
    address: '123 Main St, New York, NY 10001',
    totalOrders: 5,
    totalSpent: 2345.67,
    status: 'active',
    createdAt: new Date('2023-06-15'),
    lastOrderAt: new Date('2024-01-15'),
  },
  {
    id: 'cust-2',
    name: 'Sarah Johnson',
    email: 'sarah.j@email.com',
    phone: '+1 (555) 234-5678',
    address: '456 Oak Ave, Los Angeles, CA 90001',
    totalOrders: 3,
    totalSpent: 1567.89,
    status: 'active',
    createdAt: new Date('2023-08-20'),
    lastOrderAt: new Date('2024-01-18'),
  },
  {
    id: 'cust-3',
    name: 'Michael Brown',
    email: 'mbrown@email.com',
    phone: '+1 (555) 345-6789',
    address: '789 Pine Rd, Chicago, IL 60601',
    totalOrders: 8,
    totalSpent: 4523.45,
    status: 'active',
    createdAt: new Date('2023-03-10'),
    lastOrderAt: new Date('2024-01-20'),
  },
  {
    id: 'cust-4',
    name: 'Emily Davis',
    email: 'emily.d@email.com',
    phone: '+1 (555) 456-7890',
    address: '321 Elm St, Houston, TX 77001',
    totalOrders: 2,
    totalSpent: 980.98,
    status: 'active',
    createdAt: new Date('2023-11-05'),
    lastOrderAt: new Date('2024-01-21'),
  },
  {
    id: 'cust-5',
    name: 'David Wilson',
    email: 'dwilson@email.com',
    phone: '+1 (555) 567-8901',
    address: '654 Maple Dr, Phoenix, AZ 85001',
    totalOrders: 12,
    totalSpent: 8934.56,
    status: 'active',
    createdAt: new Date('2022-12-01'),
    lastOrderAt: new Date('2024-01-10'),
  },
  {
    id: 'cust-6',
    name: 'Lisa Anderson',
    email: 'lisa.a@email.com',
    phone: '+1 (555) 678-9012',
    address: '987 Cedar Ln, Philadelphia, PA 19101',
    totalOrders: 1,
    totalSpent: 0,
    status: 'inactive',
    createdAt: new Date('2024-01-19'),
  },
  {
    id: 'cust-7',
    name: 'Robert Taylor',
    email: 'rtaylor@email.com',
    phone: '+1 (555) 789-0123',
    address: '147 Birch Ave, San Antonio, TX 78201',
    totalOrders: 6,
    totalSpent: 3456.78,
    status: 'active',
    createdAt: new Date('2023-05-22'),
    lastOrderAt: new Date('2024-01-17'),
  },
  {
    id: 'cust-8',
    name: 'Jennifer Martinez',
    email: 'jmartinez@email.com',
    phone: '+1 (555) 890-1234',
    address: '258 Walnut St, San Diego, CA 92101',
    totalOrders: 4,
    totalSpent: 1789.34,
    status: 'active',
    createdAt: new Date('2023-09-14'),
    lastOrderAt: new Date('2024-01-21'),
  },
];

// E-Commerce stats
export const ecommerceStats = {
  totalRevenue: 45678.90,
  revenueChange: 12.5,
  totalOrders: 234,
  ordersChange: 8.3,
  totalCustomers: 156,
  customersChange: 15.2,
  averageOrderValue: 195.21,
  aovChange: 3.8,
  conversionRate: 3.2,
  conversionChange: 0.5,
};

// Revenue by month
export const ecommerceRevenueByMonth = [
  { month: 'Jan', revenue: 12500 },
  { month: 'Feb', revenue: 15200 },
  { month: 'Mar', revenue: 18900 },
  { month: 'Apr', revenue: 16400 },
  { month: 'May', revenue: 21300 },
  { month: 'Jun', revenue: 19800 },
  { month: 'Jul', revenue: 22100 },
  { month: 'Aug', revenue: 24500 },
  { month: 'Sep', revenue: 23200 },
  { month: 'Oct', revenue: 26800 },
  { month: 'Nov', revenue: 31200 },
  { month: 'Dec', revenue: 45678.90 },
];

// Alias for backward compatibility
export const revenueByMonth = ecommerceRevenueByMonth;

// Orders by status
export const ordersByStatus = [
  { status: 'Pending', count: 12, color: 'yellow' },
  { status: 'Processing', count: 28, color: 'blue' },
  { status: 'Shipped', count: 45, color: 'cyan' },
  { status: 'Delivered', count: 134, color: 'green' },
  { status: 'Cancelled', count: 15, color: 'red' },
];
